import React, { useEffect, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Helmet } from 'react-helmet';
import RevealEffects, { startStyles } from 'components/reveal-effects/RevealEffects';
import { RevealEffectConfig, RevealEffect } from 'types';

const useStyles = createUseStyles({
  dynamicContentView: {},
  '@global': {
    '.js.dcv-animate': {
      '& $dynamicContentView': {
        extend: startStyles.fade.center
      }
    }
  }
});

interface IProps {
  loading: boolean;
  dataUrls?: string[];
}

const DynamicContentView: React.FunctionComponent<IProps> = ({ dataUrls, loading, children }) => {
  const theme = useTheme();
  const styles = useStyles({ theme });
  const popstateFired = useRef<boolean>(false);
  const revealEffects: RevealEffectConfig[] = [
    { selector: `.${styles.dynamicContentView}`, revealEffect: RevealEffect.Fade }
  ];

  // dcv-animate is a class that's required to be on the document body in order
  // for animations to occur. If that class is not present, then the animations
  // don't happen. Content just appears. This hook manages adding and removing
  // the dcv-animate class based on whether or not we're navigating via a
  // back/forward browser navigation. We don't want animations to happen on
  // back/forward, so we remove the class on the popstate event. The cleanup
  // function returned by this hook checks the popstateFired ref. If it's false,
  // then we want to make sure dcv-animate is added before navigation occurs.
  // dcv-animate is originally set in gatsby-ssr
  useEffect(() => {
    function handlePopstate() {
      document.body.classList.remove('dcv-animate');
      popstateFired.current = true;
    }
    window.addEventListener('popstate', handlePopstate);

    return () => {
      window.removeEventListener('popstate', handlePopstate);
      if (!popstateFired.current) {
        document.body.classList.add('dcv-animate');
      }
    };
  }, []);

  return (
    <RevealEffects revealEffects={revealEffects} execute={!loading}>
      {dataUrls && (
        <Helmet>
          {dataUrls.map((url, i) => (
            <link rel="preload" href={url} as="fetch" key={i} crossOrigin="anonymous" />
          ))}
        </Helmet>
      )}
      <div className={styles.dynamicContentView}>{children}</div>
    </RevealEffects>
  );
};

export default DynamicContentView;
